import { ComboBoxOption } from '../components/form/controls/ComboBox';
import { ClientDto, EmployeeDto, ProjectDto, RoleDto, TeamDto } from '../api/api.generated';
import { getUnixTime } from 'date-fns';
import { toast } from 'react-toastify';
import { Status } from '../components/datagrid/ag-grid/renderStatus';

export const getComboBoxOptions = (
  list: RoleDto[] | ClientDto[] | ProjectDto[] | TeamDto[] | EmployeeDto[] | undefined | null
) => {
  if (list === undefined || list === null) return [];
  const options = list.map((item: RoleDto | ClientDto | ProjectDto | TeamDto | EmployeeDto) => getComboBoxOption(item));

  return options;
};

export const roleComboBoxOptions = (list: RoleDto[]) => {
  const options = list.map(item => getRoleComboBoxOption(item));
  return options;
};

export const getDefaultRole = (role: RoleDto | undefined) => {
  if (role === undefined || role === null) return null;
  getRoleComboBoxOption(role);
};

export const getComboBoxOption = (item: ClientDto | ProjectDto | TeamDto | EmployeeDto | RoleDto) => {
  if (isEmployee(item)) {
    return getEmployeeComboBoxOption(item as EmployeeDto);
  }
  if (isTeam(item)) {
    return getTeamComboBoxOption(item as TeamDto);
  }
  if (isRole(item)) {
    return getRoleComboBoxOption(item as RoleDto);
  }

  const option = {
    label: (item as ClientDto | ProjectDto).name,
    value: (item as ClientDto | ProjectDto).id,
  } as ComboBoxOption;

  return option;
};

const getEmployeeComboBoxOption = (employee: EmployeeDto) => {
  const option = {
    label: `${employee.firstName} ${employee.lastName}`,
    value: employee.id,
  } as ComboBoxOption;

  return option;
};

export const getTeamComboBoxOption = (team: TeamDto) => {
  const option = {
    label: `${team.teamName} (${team.managerName})`,
    value: team.managerId,
  } as ComboBoxOption;

  return option;
};

const getRoleComboBoxOption = (role: RoleDto) => {
  const option = {
    label: `${role.name}`,
    value: role.roleId,
  } as ComboBoxOption;

  return option;
};

const isEmployee = (obj: ClientDto | ProjectDto | TeamDto | EmployeeDto | RoleDto) => {
  return 'firstName' in obj;
};

const isTeam = (obj: ClientDto | ProjectDto | TeamDto | EmployeeDto | RoleDto) => {
  return 'teamName' in obj && 'managerName' in obj;
};

const isRole = (obj: ClientDto | ProjectDto | TeamDto | EmployeeDto | RoleDto) => {
  return 'roleId' in obj;
};

export const convertToClientDto = (option: ComboBoxOption) => {
  const client = {
    id: option.value,
    name: option.label,
  } as ClientDto;

  return client;
};

/**
 * Converts date to unix timestamp in milliseconds
 * @param date date
 * @returns Unix timestamp in milliseconds of converted date
 */
export function dateToUnix(date: Date) {
  const unixInSeconds = getUnixTime(date);
  const unixInMilliseconds = unixInSeconds * 1000;
  return unixInMilliseconds;
}

/**
 * Gets the priority item colour based on the priority status
 * @param status the priority status
 * @returns Colour based on priority status
 */
export const getPriorityItemColour = (status: string) => {
  if (status === Status.Complete) return 'rgba(0, 100, 0, 0.7)';
  if (status === Status.InProgress) return 'rgba(0, 0, 100, 0.7)';
  if (status === Status.LeadOnly) return 'rgba(0, 100, 215, 0.7)';
  if (status === Status.OnHold) return 'rgba(255, 125, 0, 0.7)';

  return 'rgba(255, 0, 0, 0.7)';
};

/**
 * Gets the utilisation item colour based on the utilisation value
 * @param utilisation utilisation value
 * @returns Colour based on utilisation value
 */
export const getUtilisationItemColour = (utilisation: number) => {
  if (80 <= utilisation && utilisation <= 100) return 'rgba(0, 177, 0, 0.7)';
  if (0 <= utilisation && utilisation < 80) return 'rgba(255, 177, 45, 0.7)';

  return 'rgb(255, 14, 40, 0.7)';
};

/**
 * Get a formatted error message
 * @param error Error
 * @returns Formatted error message
 */
const getErrorMessage = (error: Error) => {
  return `Error ${error.status}: ${error.response}`;
};

/**
 * The default onError handler for mutations. This method will display the error that occurred via a toast.
 * @param error error
 */
export const onMutationError = (error: Error) => {
  const message = getErrorMessage(error);
  toast.error(message);
};
